import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import BillingService from '../../../services/billingService';
import stripeService from '../../../services/stripeService';
import env from '../../../config/env';
import type { PaymentMethod } from '../../../types/billing';

interface PaymentMethodManagerProps {
  onError?: (error: string) => void;
  onSuccess?: (message: string) => void;
}

/**
 * 支払い方法管理コンポーネント
 * 
 * TODO: Stripe連携
 * 本番環境では以下のStripe機能を実装予定:
 * 1. Stripe Checkout Sessionの作成
 * 2. Stripe Customer Portalへのリダイレクト
 * 3. Webhookによる支払い情報の同期
 * 
 * 更新ボタンクリック時の処理例:
 * const handleUpdatePayment = async () => {
 *   const { url } = await stripeService.createCustomerPortalSession();
 *   window.location.href = url; // Stripeの支払い方法管理画面へ遷移
 * }
 */
const PaymentMethodManager: React.FC<PaymentMethodManagerProps> = ({
  onError,
  onSuccess,
}) => {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [loading, setLoading] = useState(true);

  // 支払い方法の取得（最初の1件のみ）
  const fetchPaymentMethod = useCallback(async () => {
    try {
      setLoading(true);
      const methods = await BillingService.getPaymentMethods();
      // 最初の支払い方法のみを表示（複数カード管理はStripe側で行う）
      if (methods && methods.length > 0) {
        setPaymentMethod(methods[0]);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '支払い方法の取得中にエラーが発生しました';
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  // 初回読み込み
  useEffect(() => {
    fetchPaymentMethod();
  }, [fetchPaymentMethod]);

  // Stripeポータルへのリダイレクト
  const handleUpdatePayment = async () => {
    try {
      // Check if Stripe is configured
      if (!stripeService.isConfigured()) {
        if (env.isDev()) {
          alert('Stripe決済画面への遷移機能は実装予定です。\n環境変数VITE_STRIPE_PUBLIC_KEYを設定してください。');
          onSuccess?.('開発環境: Stripe連携は未設定です');
        } else {
          onError?.('Stripeが設定されていません');
        }
        return;
      }

      // Redirect to Stripe Customer Portal
      await stripeService.redirectToCustomerPortal();
      onSuccess?.('Stripeカスタマーポータルにリダイレクトしています...');
    } catch (error) {
      const message = error instanceof Error ? error.message : '支払い方法の更新に失敗しました';
      onError?.(message);
    }
  };

  // ブランドロゴ・表示名の取得
  const getCardBrandDisplay = (brand: string) => {
    const brandMap = {
      visa: 'VISA',
      mastercard: 'Mastercard',
      jcb: 'JCB',
      amex: 'American Express',
    };
    return brandMap[brand as keyof typeof brandMap] || brand.toUpperCase();
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <PaymentIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>お支払い方法</Typography>
          </Box>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <PaymentIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>お支払い方法</Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleUpdatePayment}
          >
            更新
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {!paymentMethod ? (
          <Alert severity="info">
            支払い方法が登録されていません。
            <Button 
              color="primary" 
              onClick={handleUpdatePayment}
              sx={{ ml: 2 }}
            >
              支払い方法を設定
            </Button>
          </Alert>
        ) : (
          <Box display="flex" alignItems="center" gap={3}>
            <CreditCardIcon color="primary" fontSize="large" />
            
            <Box flex={1}>
              {paymentMethod.type === 'credit_card' && paymentMethod.card && (
                <>
                  <Typography variant="body1" fontWeight="medium">
                    {getCardBrandDisplay(paymentMethod.card.brand)} **** {paymentMethod.card.last4}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    有効期限: {paymentMethod.card.expMonth.toString().padStart(2, '0')}/{paymentMethod.card.expYear}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {paymentMethod.card.holderName}
                  </Typography>
                </>
              )}
              
              {paymentMethod.type === 'bank_transfer' && paymentMethod.bankTransfer && (
                <>
                  <Typography variant="body1" fontWeight="medium">
                    {paymentMethod.bankTransfer.bankName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    口座番号: **** {paymentMethod.bankTransfer.accountNumber.slice(-4)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {paymentMethod.bankTransfer.accountHolder}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        )}

        {/* Stripe連携の説明（開発環境のみ表示） */}
        {(import.meta.env.DEV || import.meta.env.DEV) && (
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>開発メモ:</strong> 本番環境ではStripe Customer Portalと連携し、
              カード情報の追加・変更・削除が安全に行えます。
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentMethodManager;